import { useToast } from '@chakra-ui/react';
import * as React from 'react';

import { getSponsorSettings } from '@/api/sponsor/sponsorSettings';
import { useCurrent } from '@/hooks/useCurrent';

const SETTINGS_ERROR = 'Unable to load sponsor settings';

export const useShouldHideUpgradeCTAButton = (): {
  shouldHideUpgradeCTAButton: boolean;
  isLoading: boolean;
} => {
  const toast = useToast();
  const current = useCurrent();
  const tenantId = current.tenant?._id;
  const [shouldHideUpgradeCTAButton, setShouldHideUpgradeCTAButton] =
    React.useState(true);
  const [isLoading, setIsLoading] = React.useState(true);

  React.useEffect(() => {
    if (current.loading) {
      return;
    }

    if (!tenantId) {
      setShouldHideUpgradeCTAButton(true);
      setIsLoading(false);
      return;
    }

    let isActive = true;
    setIsLoading(true);

    const loadSponsorSettings = async () => {
      try {
        const response = await getSponsorSettings(tenantId);
        if (!isActive) return;

        if (!response.status) {
          throw new Error(response.message || SETTINGS_ERROR);
        }

        setShouldHideUpgradeCTAButton(response.data.isUpgradeModalEnabled !== true);
      } catch (error) {
        if (!isActive) return;

        setShouldHideUpgradeCTAButton(true);
        toast({
          status: 'error',
          description: error instanceof Error ? error.message : SETTINGS_ERROR,
        });
      } finally {
        if (isActive) {
          setIsLoading(false);
        }
      }
    };

    void loadSponsorSettings();

    return () => {
      isActive = false;
    };
  }, [current.loading, tenantId, toast]);

  return { shouldHideUpgradeCTAButton, isLoading };
};
